import { logEvent } from '@amplitude/analytics-browser'
import { ErrorNotification } from '@mr-yum/frontend-ui'
import { Currency } from 'dinero.js'
import {
  AddItemsToCartDocument,
  CartBaseInput,
  CartItemPartsFragment,
  Item,
  OrderingType,
} from 'gql/graphql'
import React, { useCallback } from 'react'
import { useMutation } from 'urql'

import { ReorderDrinkItem } from './ReorderDrinkItem'

interface Props {
  availableCartItems: CartItemPartsFragment[]
  venueSlug: string
  orderingType: OrderingType
  currency: Currency
}

export const ReorderDrinksCarousel = ({
  availableCartItems,
  venueSlug,
  orderingType,
  currency,
}: Props) => {
  const [{ error }, addItemsToCart] = useMutation(AddItemsToCartDocument)

  const onAddItem = useCallback(
    async (menuItem: CartItemPartsFragment, quantityToAdd: number) => {
      const { item, upsell, quantity, cartModifiers, notes } = menuItem
      const itemId = item?.id ?? upsell?.menuItem.id
      if (!itemId) {
        return
      }

      const itemToAdd: Item = {
        item: {
          id: itemId,
          notes,
          quantity: quantityToAdd,
        },
      }

      if (cartModifiers) {
        const validModifiers = cartModifiers
          .map((cartModifier) => ({
            id: cartModifier.modifier?.id,
            quantity: cartModifier.quantity / quantity,
          }))
          .filter(
            (cartModifier): cartModifier is CartBaseInput => !!cartModifier.id,
          )

        itemToAdd.modifiers = validModifiers
      }

      const { error } = await addItemsToCart({
        input: {
          venueSlug,
          orderingType,
          items: [itemToAdd],
        },
      })

      if (!error) {
        logEvent('Reorder drinks carousel item added', {
          item: itemToAdd,
        })
      }
    },
    [addItemsToCart, venueSlug, orderingType],
  )

  if (!availableCartItems?.length) {
    return null
  }

  return (
    <div className="-mr-4 overflow-hidden">
      <span className="mb-3 my-body-sm">Order these again</span>
      <div className="flex gap-3 overflow-auto">
        {availableCartItems.map((item) => (
          <ReorderDrinkItem
            key={item.id}
            cartItem={item}
            currency={currency}
            onAddItem={onAddItem}
          />
        ))}
      </div>
      {error && <ErrorNotification fullWidth error={error} />}
    </div>
  )
}
